/**
 * GPU Management Component
 *
 * Provides an interface for managing GPU resources:
 * - List all GPUs grouped by node
 * - View GPU details and assignments
 * - Assign GPUs to tenants (exclusive or shared mode)
 * - Update and remove assignments
 *
 * Access: GLOBAL_ADMIN only
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiClient, isSuccess } from '../../api/client';
import { Layout } from '../layout/Layout';

// =============================================================================
// Types
// =============================================================================

type GPUStatus = 'AVAILABLE' | 'IN_USE' | 'ERROR' | 'OFFLINE';
type GPUAssignmentMode = 'EXCLUSIVE' | 'SHARED';

interface GPUAssignment {
  id: string;
  tenantId: string;
  tenantName: string;
  mode: GPUAssignmentMode;
  quotaPercent: number | null;
  memoryLimitMb: number | null;
  priority: number;
}

interface GPU {
  id: string;
  nodeId: string;
  nodeName: string;
  deviceIndex: number;
  uuid: string | null;
  model: string | null;
  memoryMb: number;
  vendor: string;
  driverVersion: string | null;
  status: GPUStatus;
  assignments: GPUAssignment[];
}

interface GPUsResponse {
  gpus: GPU[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

interface GPUStatsResponse {
  totalGPUs: number;
  availableGPUs: number;
  inUseGPUs: number;
  offlineGPUs: number;
  errorGPUs: number;
  byVendor: Record<string, number>;
  totalVRAMGb: number;
  assignedGPUs: number;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
}

interface AssignFormData {
  tenantId: string;
  mode: GPUAssignmentMode;
  quotaPercent: number;
  memoryLimitMb: number;
  priority: number;
}

type ModalState =
  | { type: 'none' }
  | { type: 'assign'; gpu: GPU }
  | { type: 'details'; gpu: GPU }
  | { type: 'removeAssignment'; assignment: GPUAssignment; gpu: GPU };

// =============================================================================
// Constants
// =============================================================================

const STATUS_COLORS: Record<GPUStatus, { bg: string; text: string; dot: string }> = {
  AVAILABLE: { bg: 'bg-green-900/20', text: 'text-green-400', dot: 'bg-green-400' },
  IN_USE: { bg: 'bg-blue-900/20', text: 'text-blue-400', dot: 'bg-blue-400' },
  ERROR: { bg: 'bg-red-900/20', text: 'text-red-400', dot: 'bg-red-400' },
  OFFLINE: { bg: 'bg-gray-900/20', text: 'text-gray-400', dot: 'bg-gray-400' },
};

const STATUS_LABELS: Record<GPUStatus, string> = {
  AVAILABLE: 'Available',
  IN_USE: 'In Use',
  ERROR: 'Error',
  OFFLINE: 'Offline',
};

const VENDOR_COLORS: Record<string, { bg: string; text: string }> = {
  nvidia: { bg: 'bg-green-900/30', text: 'text-green-400' },
  amd: { bg: 'bg-red-900/30', text: 'text-red-400' },
  intel: { bg: 'bg-blue-900/30', text: 'text-blue-400' },
};

const MODE_LABELS: Record<GPUAssignmentMode, string> = {
  EXCLUSIVE: 'Exclusive',
  SHARED: 'Shared',
};

// =============================================================================
// Sub-components
// =============================================================================

function StatusBadge({ status }: { status: GPUStatus }) {
  const colors = STATUS_COLORS[status];
  return (
    <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}>
      <div className={`h-1.5 w-1.5 rounded-full ${colors.dot}`} />
      {STATUS_LABELS[status]}
    </div>
  );
}

function VendorBadge({ vendor }: { vendor: string }) {
  const colors = VENDOR_COLORS[vendor.toLowerCase()] || { bg: 'bg-gray-900/30', text: 'text-gray-400' };
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium uppercase ${colors.bg} ${colors.text}`}>
      {vendor}
    </span>
  );
}

function ModeBadge({ mode }: { mode: GPUAssignmentMode }) {
  const colors = mode === 'EXCLUSIVE'
    ? { bg: 'bg-purple-900/30', text: 'text-purple-400' }
    : { bg: 'bg-cyan-900/30', text: 'text-cyan-400' };

  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${colors.bg} ${colors.text}`}>
      {MODE_LABELS[mode]}
    </span>
  );
}

function StatCard({ label, value, color, subtitle }: { label: string; value: number | string; color: string; subtitle?: string }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-4">
      <p className="text-sm text-gray-400">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${color}`}>{value}</p>
      {subtitle && <p className="mt-1 text-xs text-gray-500">{subtitle}</p>}
    </div>
  );
}

function formatMemory(mb: number): string {
  if (mb >= 1024) {
    return `${(mb / 1024).toFixed(1)} GB`;
  }
  return `${mb} MB`;
}

// =============================================================================
// Main Component
// =============================================================================

export function GPUManagement() {
  const { user } = useAuth();
  const api = useMemo(() => getApiClient(), []);

  // State
  const [gpus, setGpus] = useState<GPU[]>([]);
  const [stats, setStats] = useState<GPUStatsResponse | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>({ type: 'none' });

  // Filters
  const [statusFilter, setStatusFilter] = useState<GPUStatus | ''>('');
  const [vendorFilter, setVendorFilter] = useState<string>('');
  const [unassignedOnly, setUnassignedOnly] = useState(false);

  // Form state
  const [formData, setFormData] = useState<AssignFormData>({
    tenantId: '',
    mode: 'EXCLUSIVE',
    quotaPercent: 100,
    memoryLimitMb: 0,
    priority: 0,
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const isGlobalAdmin = user?.role === 'GLOBAL_ADMIN';

  // Fetch GPUs
  const fetchGPUs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (vendorFilter) params.set('vendor', vendorFilter);
      if (unassignedOnly) params.set('unassignedOnly', 'true');

      const response = await api.get<GPUsResponse>(
        `/nodes/gpus?${params.toString()}`
      );

      if (isSuccess(response)) {
        setGpus(response.data.gpus);
      } else {
        setError('Failed to load GPUs');
      }
    } catch {
      setError('Failed to load GPUs');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter, vendorFilter, unassignedOnly]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get<GPUStatsResponse>('/nodes/gpus/stats');

      if (isSuccess(response)) {
        setStats(response.data);
      }
    } catch {
      // Non-critical
    }
  }, [api]);

  // Fetch tenants
  const fetchTenants = useCallback(async () => {
    try {
      const response = await api.get<{ tenants: Tenant[] }>('/tenants');

      if (isSuccess(response)) {
        setTenants(response.data.tenants);
      }
    } catch {
      // Non-critical
    }
  }, [api]);

  // Initial load - component is inside ProtectedRoute so auth is guaranteed
  useEffect(() => {
    fetchStats();
    fetchTenants();
    fetchGPUs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch GPUs when filters change
  useEffect(() => {
    // Skip initial render (handled above)
    if (loading) return;
    fetchGPUs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, vendorFilter, unassignedOnly]);

  // Handle assign GPU
  const handleAssign = async () => {
    if (modal.type !== 'assign') return;

    setFormLoading(true);
    setFormError(null);

    try {
      const payload = {
        tenantId: formData.tenantId,
        mode: formData.mode,
        priority: formData.priority,
        ...(formData.mode === 'SHARED' && {
          quotaPercent: formData.quotaPercent,
          memoryLimitMb: formData.memoryLimitMb || undefined,
        }),
      };

      const response = await api.post<{ success: boolean }>(
        `/nodes/gpus/${modal.gpu.id}/assign`,
        payload
      );

      if (isSuccess(response) && response.data.success) {
        setModal({ type: 'none' });
        fetchGPUs();
        fetchStats();
      } else {
        setFormError('Failed to assign GPU');
      }
    } catch {
      setFormError('Failed to assign GPU');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle remove assignment
  const handleRemoveAssignment = async (gpuId: string, assignmentId: string) => {
    try {
      const response = await api.delete<{ success: boolean }>(
        `/nodes/gpus/${gpuId}/assignments/${assignmentId}`
      );

      if (isSuccess(response) && response.data.success) {
        setModal({ type: 'none' });
        fetchGPUs();
        fetchStats();
      } else {
        setError('Failed to remove assignment');
      }
    } catch {
      setError('Failed to remove assignment');
    }
  };

  // Group GPUs by node
  const gpusByNode = gpus.reduce((acc, gpu) => {
    if (!acc[gpu.nodeId]) {
      acc[gpu.nodeId] = { name: gpu.nodeName, gpus: [] };
    }
    acc[gpu.nodeId].gpus.push(gpu);
    return acc;
  }, {} as Record<string, { name: string; gpus: GPU[] }>);

  if (!isGlobalAdmin) {
    return (
      <Layout>
        <div className="flex h-full items-center justify-center">
          <p className="text-gray-400">Access denied. Global admin privileges required.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-white">GPU Management</h1>
          <p className="mt-1 text-sm text-gray-400">
            View and assign GPU resources to tenants
          </p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-5 gap-4">
            <StatCard label="Total GPUs" value={stats.totalGPUs} color="text-white" />
            <StatCard label="Available" value={stats.availableGPUs} color="text-green-400" />
            <StatCard label="In Use" value={stats.inUseGPUs} color="text-blue-400" />
            <StatCard label="Assigned" value={stats.assignedGPUs} color="text-purple-400" />
            <StatCard label="Total VRAM" value={`${stats.totalVRAMGb} GB`} color="text-cyan-400" />
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as GPUStatus | '')}
            className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:border-indigo-500 focus:outline-none"
          >
            <option value="">All Statuses</option>
            <option value="AVAILABLE">Available</option>
            <option value="IN_USE">In Use</option>
            <option value="OFFLINE">Offline</option>
            <option value="ERROR">Error</option>
          </select>

          <select
            value={vendorFilter}
            onChange={(e) => setVendorFilter(e.target.value)}
            className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:border-indigo-500 focus:outline-none"
          >
            <option value="">All Vendors</option>
            <option value="nvidia">NVIDIA</option>
            <option value="amd">AMD</option>
            <option value="intel">Intel</option>
          </select>

          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={unassignedOnly}
              onChange={(e) => setUnassignedOnly(e.target.checked)}
              className="rounded border-gray-600 bg-gray-800 text-indigo-600 focus:ring-indigo-500"
            />
            Unassigned only
          </label>
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-lg border border-red-500/50 bg-red-500/10 p-4 text-red-400">
            {error}
          </div>
        )}

        {/* GPU List by Node */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
          </div>
        ) : gpus.length === 0 ? (
          <div className="rounded-lg border border-white/10 bg-white/5 p-8 text-center">
            <p className="text-gray-400">No GPUs found</p>
            <p className="mt-2 text-sm text-gray-500">
              Register remote nodes to see their GPUs
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(gpusByNode).map(([nodeId, { name, gpus: nodeGpus }]) => (
              <div key={nodeId} className="rounded-lg border border-white/10 overflow-hidden">
                <div className="bg-white/5 px-4 py-3 border-b border-white/10">
                  <h3 className="font-medium text-white">{name}</h3>
                  <p className="text-sm text-gray-400">{nodeGpus.length} GPU(s)</p>
                </div>
                <div className="divide-y divide-white/5">
                  {nodeGpus.map((gpu) => (
                    <div key={gpu.id} className="p-4 hover:bg-white/5">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white/5 text-gray-400">
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                            </svg>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-white">
                                GPU {gpu.deviceIndex}
                              </span>
                              <VendorBadge vendor={gpu.vendor} />
                              <StatusBadge status={gpu.status} />
                            </div>
                            <p className="mt-1 text-sm text-gray-400">
                              {gpu.model || 'Unknown Model'} - {formatMemory(gpu.memoryMb)}
                            </p>
                            {gpu.uuid && (
                              <p className="text-xs text-gray-500 font-mono">{gpu.uuid}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {gpu.status === 'AVAILABLE' && (
                            <button
                              onClick={() => {
                                setFormData({
                                  tenantId: '',
                                  mode: 'EXCLUSIVE',
                                  quotaPercent: 100,
                                  memoryLimitMb: gpu.memoryMb,
                                  priority: 0,
                                });
                                setModal({ type: 'assign', gpu });
                              }}
                              className="rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500"
                            >
                              Assign
                            </button>
                          )}
                          <button
                            onClick={() => setModal({ type: 'details', gpu })}
                            className="rounded bg-white/10 px-3 py-1.5 text-xs text-gray-300 hover:bg-white/20"
                          >
                            Details
                          </button>
                        </div>
                      </div>

                      {/* Assignments */}
                      {gpu.assignments.length > 0 && (
                        <div className="mt-4 ml-16 space-y-2">
                          <p className="text-xs uppercase text-gray-500">Assignments</p>
                          {gpu.assignments.map((assignment) => (
                            <div
                              key={assignment.id}
                              className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2"
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-sm text-white">{assignment.tenantName}</span>
                                <ModeBadge mode={assignment.mode} />
                                {assignment.mode === 'SHARED' && assignment.quotaPercent && (
                                  <span className="text-xs text-gray-400">
                                    {assignment.quotaPercent}% quota
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() => setModal({ type: 'removeAssignment', assignment, gpu })}
                                className="text-xs text-red-400 hover:text-red-300"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Assign Modal */}
        {modal.type === 'assign' && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-lg border border-white/10 bg-gray-900 p-6">
              <h2 className="text-lg font-semibold text-white">Assign GPU</h2>
              <p className="mt-1 text-sm text-gray-400">
                GPU {modal.gpu.deviceIndex} on {modal.gpu.nodeName}
              </p>

              <div className="mt-6 space-y-4">
                {/* Tenant */}
                <div>
                  <label className="block text-sm font-medium text-gray-300">Tenant</label>
                  <select
                    value={formData.tenantId}
                    onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white focus:border-indigo-500 focus:outline-none"
                  >
                    <option value="">Select a tenant...</option>
                    {tenants.map((tenant) => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Mode */}
                <div>
                  <label className="block text-sm font-medium text-gray-300">Assignment Mode</label>
                  <div className="mt-2 flex gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="EXCLUSIVE"
                        checked={formData.mode === 'EXCLUSIVE'}
                        onChange={(e) => setFormData({ ...formData, mode: e.target.value as GPUAssignmentMode })}
                        className="text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-300">Exclusive</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="SHARED"
                        checked={formData.mode === 'SHARED'}
                        onChange={(e) => setFormData({ ...formData, mode: e.target.value as GPUAssignmentMode })}
                        className="text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-300">Shared</span>
                    </label>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.mode === 'EXCLUSIVE'
                      ? 'Tenant has full access to this GPU'
                      : 'Multiple tenants can share this GPU with quotas'}
                  </p>
                </div>

                {/* Shared mode options */}
                {formData.mode === 'SHARED' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-300">
                        Quota ({formData.quotaPercent}%)
                      </label>
                      <input
                        type="range"
                        min={1}
                        max={100}
                        value={formData.quotaPercent}
                        onChange={(e) => setFormData({ ...formData, quotaPercent: Number(e.target.value) })}
                        className="mt-1 w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300">
                        Memory Limit (MB)
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={modal.gpu.memoryMb}
                        value={formData.memoryLimitMb}
                        onChange={(e) => setFormData({ ...formData, memoryLimitMb: Number(e.target.value) })}
                        placeholder={`Max: ${modal.gpu.memoryMb}`}
                        className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white focus:border-indigo-500 focus:outline-none"
                      />
                    </div>
                  </>
                )}

                {/* Priority */}
                <div>
                  <label className="block text-sm font-medium text-gray-300">Priority (0-100)</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: Number(e.target.value) })}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white focus:border-indigo-500 focus:outline-none"
                  />
                  <p className="mt-1 text-xs text-gray-500">Higher priority = preference in scheduling</p>
                </div>

                {formError && <div className="text-sm text-red-400">{formError}</div>}
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setModal({ type: 'none' })}
                  className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssign}
                  disabled={formLoading || !formData.tenantId}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
                >
                  {formLoading ? 'Assigning...' : 'Assign GPU'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Details Modal */}
        {modal.type === 'details' && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-lg rounded-lg border border-white/10 bg-gray-900 p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">GPU Details</h2>
                <StatusBadge status={modal.gpu.status} />
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-400">Node</p>
                  <p className="text-white">{modal.gpu.nodeName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Device Index</p>
                  <p className="text-white">{modal.gpu.deviceIndex}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Model</p>
                  <p className="text-white">{modal.gpu.model || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Memory</p>
                  <p className="text-white">{formatMemory(modal.gpu.memoryMb)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Vendor</p>
                  <p className="text-white capitalize">{modal.gpu.vendor}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Driver</p>
                  <p className="text-white">{modal.gpu.driverVersion || 'Unknown'}</p>
                </div>
                {modal.gpu.uuid && (
                  <div className="col-span-2">
                    <p className="text-sm text-gray-400">UUID</p>
                    <p className="text-white font-mono text-xs break-all">{modal.gpu.uuid}</p>
                  </div>
                )}
              </div>

              {modal.gpu.assignments.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-300">Current Assignments</h3>
                  <div className="mt-2 space-y-2">
                    {modal.gpu.assignments.map((a) => (
                      <div key={a.id} className="flex items-center justify-between rounded-lg bg-white/5 p-3">
                        <div>
                          <p className="text-white">{a.tenantName}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <ModeBadge mode={a.mode} />
                            {a.quotaPercent && <span className="text-xs text-gray-400">{a.quotaPercent}%</span>}
                          </div>
                        </div>
                        <button
                          onClick={() => setModal({ type: 'removeAssignment', assignment: a, gpu: modal.gpu })}
                          className="text-sm text-red-400 hover:text-red-300"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setModal({ type: 'none' })}
                  className="rounded-lg bg-white/10 px-4 py-2 text-sm text-gray-300 hover:bg-white/20"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Remove Assignment Modal */}
        {modal.type === 'removeAssignment' && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-lg border border-white/10 bg-gray-900 p-6">
              <div className="flex items-center gap-3 text-red-400">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h2 className="text-lg font-semibold">Remove Assignment</h2>
              </div>

              <p className="mt-4 text-gray-300">
                Remove {modal.assignment.tenantName}'s assignment from GPU {modal.gpu.deviceIndex}?
              </p>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setModal({ type: 'none' })}
                  className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRemoveAssignment(modal.gpu.id, modal.assignment.id)}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-500"
                >
                  Remove Assignment
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default GPUManagement;
